// ICU message runtime [02 §12; MB D17]. One namespace per lane file (packages/i18n/locales/<lng>/<ns>.json),
// discovered by `pnpm gen` into `localeCatalogues`. Keys are full dotted ids (`errors.code.NOT_FOUND`,
// `ctx.screen.element`); a namespace file may store them flat or nested. Missing keys fall back to EN, then to the key.
import { DOCUMENT_LANGUAGE } from '@skm/core';
import { IntlMessageFormat } from 'intl-messageformat';

export const UI_LOCALES = Object.freeze(['en', 'fr'] as const);
export type UiLocale = (typeof UI_LOCALES)[number];
/** Customer-document languages = the core enum `core.data_translation.lang` (AC-KRN-04: no redefinition). */
export const DOCUMENT_LOCALES = DOCUMENT_LANGUAGE.values;
export type DocumentLocale = (typeof DOCUMENT_LOCALES)[number];
export type AnyLocale = DocumentLocale;

/** BCP-47 tag used for Intl / ICU formatting (02 §12 Formats). */
export const INTL_TAG: Readonly<Record<AnyLocale, string>> = Object.freeze({
  en: 'en-CA',
  fr: 'fr-CA',
  de: 'de-DE',
});

export type CatalogueTree = { readonly [key: string]: string | CatalogueTree };
export type Catalogues = Partial<Record<AnyLocale, Readonly<Record<string, CatalogueTree>>>>;
export type MessageParams = Record<string, string | number | boolean | Date | null | undefined>;

export function isUiLocale(value: unknown): value is UiLocale {
  return typeof value === 'string' && (UI_LOCALES as readonly string[]).includes(value);
}

/** Picks the UI locale: user setting, then Accept-Language, then `en` (02 §12 Server text). */
export function resolveUiLocale(
  userLocale?: string | null,
  acceptLanguage?: string | null,
): UiLocale {
  if (isUiLocale(userLocale)) return userLocale;
  for (const part of (acceptLanguage ?? '').split(',')) {
    const tag = part.split(';')[0]?.trim().toLowerCase().slice(0, 2);
    if (isUiLocale(tag)) return tag;
  }
  return 'en';
}

/** Flattens a nested catalogue into dotted keys. Non-string leaves are ignored (parity reports them). */
export function flattenTree(tree: CatalogueTree, prefix = '', out = new Map<string, string>()) {
  for (const [k, v] of Object.entries(tree)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'string') out.set(key, v);
    else if (v !== null && typeof v === 'object') flattenTree(v, key, out);
  }
  return out;
}

/** Merges every namespace of one locale; `common` first. Returns duplicate keys too (lanes must not collide). */
export function mergeNamespaces(namespaces: Readonly<Record<string, CatalogueTree>> | undefined) {
  const messages = new Map<string, string>();
  const duplicates: string[] = [];
  const names = Object.keys(namespaces ?? {}).sort((a, b) =>
    a === 'common' ? -1 : b === 'common' ? 1 : a.localeCompare(b),
  );
  for (const ns of names) {
    const tree = namespaces?.[ns];
    if (!tree) continue;
    for (const [k, v] of flattenTree(tree)) {
      if (messages.has(k)) duplicates.push(`${ns}:${k}`);
      else messages.set(k, v);
    }
  }
  return { messages, duplicates };
}

export interface Translator {
  readonly locale: AnyLocale;
  t(key: string, params?: MessageParams): string;
  has(key: string): boolean;
}

export interface TranslatorOptions {
  readonly catalogues: Catalogues;
  readonly locale: AnyLocale;
  /** Called once per missing key (after EN fallback failed or when falling back). */
  readonly onMissing?: (key: string, locale: AnyLocale) => void;
}

export function createTranslator(opts: TranslatorOptions): Translator {
  const own = mergeNamespaces(opts.catalogues[opts.locale]).messages;
  const fallback = opts.locale === 'en' ? own : mergeNamespaces(opts.catalogues.en).messages;
  const cache = new Map<string, IntlMessageFormat>();
  const tag = INTL_TAG[opts.locale];
  const format = (key: string, message: string, params?: MessageParams): string => {
    let mf = cache.get(key);
    if (!mf) {
      mf = new IntlMessageFormat(message, tag, undefined, { ignoreTag: true });
      cache.set(key, mf);
    }
    const out = mf.format(params as Record<string, string | number | boolean | Date>);
    return Array.isArray(out) ? out.join('') : String(out);
  };
  return Object.freeze({
    locale: opts.locale,
    has: (key: string) => own.has(key),
    t(key: string, params?: MessageParams): string {
      const message = own.get(key);
      if (message !== undefined) return format(key, message, params);
      opts.onMissing?.(key, opts.locale);
      const en = fallback.get(key);
      return en === undefined ? key : format(`en:${key}`, en, params);
    },
  });
}
